/**
 * Staff Performance Engine
 * Calculates weighted performance scores for staff members based on their assigned entities
 *
 * Formula: Performance Score = Sum(Weight × Score) / Sum(Weight) × 100
 */

class StaffPerformanceEngine {
  /**
   * Calculate performance for all staff members on an asset
   *
   * @param {Array} entities - All entities for the asset
   * @param {Array} latestSignals - Latest signals mapped by entity ID
   * @returns {Array} Staff performance data
   */
  calculateStaffPerformance(entities, latestSignals) {
    console.log("[StaffPerformanceEngine] Calculating staff performance");

    // Group entities by owner
    const entitiesByOwner = this.groupEntitiesByOwner(entities);

    // Calculate performance for each staff member
    const staffPerformance = [];

    for (const [ownerName, ownerEntities] of entitiesByOwner.entries()) {
      const performance = this.calculateOwnerPerformance(
        ownerName,
        ownerEntities,
        latestSignals,
      );
      console.log(
        `[StaffPerformanceEngine] Performance for ${ownerName}:`,
        performance,
      );
      if (performance) {
        staffPerformance.push(performance);
      }
    }

    // Sort by performance score descending
    staffPerformance.sort((a, b) => b.performanceScore - a.performanceScore);

    return staffPerformance;
  }

  /**
   * Group entities by owner
   */
  groupEntitiesByOwner(entities) {
    const groups = new Map();

    entities.forEach((entity) => {
      // Only include entities with both Owner and Weight
      if (entity.Owner && entity.Weight) {
        const ownerName = this.extractOwnerName(entity.Owner);

        if (!groups.has(ownerName)) {
          groups.set(ownerName, []);
        }

        groups.get(ownerName).push(entity);
      }
    });

    return groups;
  }

  /**
   * Extract owner name from SharePoint Person field
   */
  extractOwnerName(owner) {
    if (typeof owner === "string") {
      return owner;
    }

    // SharePoint Person field might be object: { Title: "John Doe", Email: "..." }
    if (owner && owner.Title) {
      return owner.Title;
    }

    return "Unknown";
  }

  /**
   * Calculate performance for a single owner
   */
  calculateOwnerPerformance(ownerName, entities, latestSignals) {
    const assignments = [];
    let totalWeight = 0;
    let totalWeightedScore = 0;
    let scoredItems = 0;

    entities.forEach((entity) => {
      const weight = entity.Weight || 0;
      totalWeight += weight;

      // Get latest score signal for this entity
      console.log(latestSignals);
      const signal = latestSignals.get(entity.Id);
      console.log(
        `[StaffPerformanceEngine] Latest signal for entity ${entity.Id} (${entity.Title}):`,
        signal,
      );
      let score = null;
      let weightedScore = 0;
      let status = "Not Scored";

      if (
        signal &&
        signal.SignalType === "Score" &&
        signal.NumericValue != null
      ) {
        score = signal.NumericValue; // 0-1 range
        weightedScore = weight * score;
        totalWeightedScore += weightedScore;
        scoredItems++;

        // Determine status based on score
        if (score >= 0.9) status = "Excellent";
        else if (score >= 0.75) status = "Good";
        else if (score >= 0.5) status = "Fair";
        else status = "Poor";
      }

      assignments.push({
        entityId: entity.Id,
        entityTitle: entity.Title,
        entityType: entity.EntityType,
        weight: weight,
        score: score,
        weightedScore: weightedScore,
        status: status,
        lastUpdated: signal ? signal.Timestamp : null,
      });
    });

    // Calculate overall performance score (0-100)
    const performanceScore =
      totalWeight > 0 ? (totalWeightedScore / totalWeight) * 100 : 0;

    // Determine overall status
    let overallStatus = "Not Scored";
    if (scoredItems > 0) {
      if (performanceScore >= 90) overallStatus = "Excellent";
      else if (performanceScore >= 75) overallStatus = "Good";
      else if (performanceScore >= 50) overallStatus = "Fair";
      else overallStatus = "Needs Improvement";
    }

    return {
      ownerName: ownerName,
      totalWeight: totalWeight,
      totalWeightedScore: totalWeightedScore,
      performanceScore: performanceScore,
      overallStatus: overallStatus,
      assignmentsCount: entities.length,
      scoredCount: scoredItems,
      unscoredCount: entities.length - scoredItems,
      completionRate:
        entities.length > 0 ? (scoredItems / entities.length) * 100 : 0,
      assignments: assignments,
    };
  }

  /**
   * Get performance for a specific staff member
   */
  getStaffMemberPerformance(staffName, entities, latestSignals) {
    const ownerEntities = entities.filter((e) => {
      const ownerName = this.extractOwnerName(e.Owner);
      return ownerName === staffName && e.Weight;
    });

    if (ownerEntities.length === 0) {
      return null;
    }

    return this.calculateOwnerPerformance(
      staffName,
      ownerEntities,
      latestSignals,
    );
  }

  /**
   * Get performance summary statistics
   */
  getPerformanceSummary(staffPerformance) {
    if (staffPerformance.length === 0) {
      return {
        staffCount: 0,
        averagePerformance: 0,
        topPerformer: null,
        needsAttention: [],
      };
    }

    const totalPerformance = staffPerformance.reduce(
      (sum, staff) => sum + staff.performanceScore,
      0,
    );

    const averagePerformance = totalPerformance / staffPerformance.length;

    const topPerformer = staffPerformance[0]; // Already sorted descending

    const needsAttention = staffPerformance.filter(
      (staff) => staff.performanceScore < 50,
    );

    return {
      staffCount: staffPerformance.length,
      averagePerformance: averagePerformance,
      topPerformer: topPerformer,
      needsAttention: needsAttention,
    };
  }
}

export default new StaffPerformanceEngine();
